import { useRef, useEffect, useCallback, useState } from 'react';
import { useAppContext } from '../context/context';

type UseScrollPositionOptions = {
  keyForPersist?: string;
  restoreOnMount?: boolean;
  storage?: 'session' | 'context';
};

/**
 * useScrollPosition is a custom React hook for managing and persisting the scroll position
 * of a scrollable element. It supports saving and restoring scroll position using either
 * sessionStorage or a context-based storage, and can automatically restore the scroll
 * position on mount.
 *
 * @param {Object} options - Configuration options for the hook.
 * @param {string} [options.keyForPersist] - The key used to persist the scroll position. if not provided, the scroll position will not be persisted.
 * @param {boolean} [options.restoreOnMount=true] - Whether to restore scroll position on mount.
 * @param {'session'|'context'} [options.storage='context'] - Where to persist the scroll position (effectively, if it persists across page reloads).
 *
 * @returns {{
 *   scrollRef: React.MutableRefObject<HTMLElement | null>,
 *   saveScroll: () => void,
 *   restoreScroll: () => void,
 *   scrollToBottom: () => void
 * }}
 *
 * @example
 * ```tsx
 *   const { scrollRef, saveScroll, restoreScroll } = useScrollPosition({
 *     keyForPersist: 'my-scroll-key',
 *     storage: 'session'
 *   });
 *   // Attach scrollRef to the scrollable element.
 *   // Call saveScroll on scroll events, restoreScroll when needed.
 * ```
 */
export function useScrollPosition({
  keyForPersist,
  restoreOnMount = true,
  storage = 'context'
}: UseScrollPositionOptions = {}) {
  const { scrollPos, setScrollPos } = useAppContext();
  const scrollRef = useRef<HTMLElement | null>(null);
  const [localScrollPos, setLocalScrollPos] = useState(0);

  /**
   * Saves the current scroll position of the referenced element.
   * Persists the value in sessionStorage or context, depending on the storage option.
   */
  const saveScroll = useCallback(() => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      if (keyForPersist) {
        if (storage === 'session') {
          sessionStorage.setItem(keyForPersist, String(scrollTop));
        } else {
          setScrollPos(prev => ({ ...prev, [keyForPersist]: scrollTop }));
        }
      }
      setLocalScrollPos(scrollTop);
    }
  }, [keyForPersist, storage, scrollPos, setScrollPos]);

  /**
   * Restores the scroll position of the referenced element from persisted storage.
   * If no persisted value is found, defaults to 0 or the last locally saved value.
   */
  const restoreScroll = useCallback(() => {
    if (scrollRef.current) {
      let scrollTop = 0;
      if (keyForPersist) {
        if (storage === 'session') {
          const stored = sessionStorage.getItem(keyForPersist);
          if (stored) scrollTop = parseInt(stored, 10);
        } else {
          scrollTop = scrollPos?.[keyForPersist] || 0;
        }
        scrollRef.current.scrollTop = scrollTop;
      } else {
        scrollRef.current.scrollTop = localScrollPos;
      }
    }
  }, [keyForPersist, storage, scrollPos, localScrollPos]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);
  // Automatically restore scroll position on mount if restoreOnMount is true.
  useEffect(() => {
    if (restoreOnMount) {
      setTimeout(restoreScroll, 100); // Wait for DOM
    }
  }, [restoreOnMount, restoreScroll]);

  return {
    scrollRef,
    saveScroll,
    restoreScroll,
    scrollToBottom
  };
}
